import { teamAbbr, type TeamId } from './teams'

/**
 * Club colours — the only place club colour exists as data. `teams.ts` carries identity; the hexes
 * in the shield SVGs are baked into markup.
 *
 * **Not tokens.** 22 clubs times two values is data; `tokens.css` is a theme. Keyed on the
 * three-letter abbreviation exactly as `clubBanners.ts` keys shields.
 *
 * Exactly **two** named inks per club: which colour is secondary is a judgement call, picked once
 * by eye. Nothing is conditioned on theme or background — where a segment reads poorly, the fix is
 * the value here, not a runtime contrast rule.
 *
 * Leicester and West Ham are absent: both are `isActive: false` in `teams.ts`.
 */
export interface ClubInks {
  primary: string
  /** Used by the away side only, and only when its primary clashes with the home side's. */
  secondary: string
}

/** Source of truth: `docs/team abbrevation and colors.rtf`. */
const INKS_BY_ABBR: Record<string, ClubInks> = {
  ARS: { primary: '#EF0107', secondary: '#063672' },
  // Claret over the sheet's listed blue; the blue it displaces is the escape.
  AVL: { primary: '#670E36', secondary: '#95BFE5' },
  // Third listed, skipping white.
  BHA: { primary: '#0057B8', secondary: '#FFCD00' },
  BOU: { primary: '#B50E12', secondary: '#000000' },
  BRE: { primary: '#D20000', secondary: '#FFFFFF' },
  CHE: { primary: '#034694', secondary: '#DBA111' },
  COV: { primary: '#059DD9', secondary: '#FFFFFF' },
  // Red over blue — the sash reads red first.
  CRY: { primary: '#C4122E', secondary: '#1B458F' },
  EVE: { primary: '#003399', secondary: '#FFFFFF' },
  // White, the sheet's third colour. The sheet's own note says use red for the abbreviation.
  FUL: { primary: '#FFFFFF', secondary: '#CC0000' },
  // Tiger orange leads; black is the escape (swapped the pair).
  HUL: { primary: '#F18A01', secondary: '#000000' },
  IPS: { primary: '#66A8CC', secondary: '#E40520' },
  // White shirts, the sheet's third colour; the blue stays as the escape.
  LEE: { primary: '#FFFFFF', secondary: '#1D428A' },
  LIV: { primary: '#C8102E', secondary: '#00B2A9' },
  MCI: { primary: '#6CABDD', secondary: '#1C2C5B' },
  MUN: { primary: '#DA291C', secondary: '#FBE122' },
  NEW: { primary: '#241F20', secondary: '#FFFFFF' },
  // Doc order reversed: it lists white first, and Forest play in red.
  NFO: { primary: '#DD0000', secondary: '#FFFFFF' },
  // Third listed, skipping white.
  SUN: { primary: '#EB172C', secondary: '#211E1E' },
  // White shirts lead; the navy is the escape (swapped the pair).
  TOT: { primary: '#FFFFFF', secondary: '#132257' },
}

/**
 * The last resort when even the away side's secondary clashes.
 *
 * A real case only once a missing row is filled badly; it should never render in practice.
 */
export const INK_FALLBACK = 'var(--color-text-secondary)'

/** A club's two inks, or undefined for anything the table doesn't carry. */
export function clubInks(team: TeamId | string): ClubInks | undefined {
  return INKS_BY_ABBR[teamAbbr(team)]
}

// ── Clash detection ───────────────────────────────────────────────────────────────────────────

/** Below this chroma an ink is a grey/black/white, and hue says nothing useful about it. */
const ACHROMATIC_CHROMA = 0.05
/** Two chromatic inks clash inside this many degrees of hue. */
const CLASH_HUE_DEGREES = 25
/**
 * ...unless their OKLCH lightness differs by at least this much.
 *
 * Hue alone called City's sky blue the same colour as Chelsea's, Everton's and Brighton's deep
 * blues, so City rendered as the grey fallback in three fixtures. Pale-against-deep is not the
 * failure the hue rule exists to catch.
 *
 * 0.20 sits in clear air: the closest pair it frees is City against Brighton at 0.245, and the
 * widest red pair it must keep clashing is United against Bournemouth at 0.084.
 */
const CLASH_LIGHTNESS_ESCAPE = 0.2
/**
 * Two achromatic inks clash inside this much OKLCH lightness.
 *
 * 0.15 is the obvious number and the wrong one — it is not written against OKLCH's lightness
 * scale. Newcastle's `#241F20` sits at L 0.245 and Bournemouth's `#000000` at 0, so the very pair
 * this rule exists to catch would sail past a 0.15 gate. 0.30 is the OKLCH equivalent
 * — it catches every near-black pairing in the table and still leaves white against black alone,
 * which is the behaviour the ticket describes.
 */
const CLASH_LIGHTNESS = 0.3

export interface Oklch {
  l: number
  c: number
  /** Degrees, 0-360. Meaningless when `c` is below `ACHROMATIC_CHROMA`. */
  h: number
}

/** `#RRGGBB` (or `#RGB`) → 0-1 channels, or null for anything that isn't a hex colour. */
function parseHex(hex: string): [number, number, number] | null {
  const value = hex.trim().replace('#', '')
  const full =
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null
  return [
    Number.parseInt(full.slice(0, 2), 16) / 255,
    Number.parseInt(full.slice(2, 4), 16) / 255,
    Number.parseInt(full.slice(4, 6), 16) / 255,
  ]
}

function toLinear(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

/**
 * sRGB hex → OKLCH.
 *
 * OKLCH rather than HSL because the clash rule is about *perceived* similarity: HSL calls Arsenal
 * red and Bournemouth red 5° apart and two different blacks 0° apart with wildly
 * different lightness, and a perceptual space is what makes one threshold work for both.
 */
export function hexToOklch(hex: string): Oklch | null {
  const rgb = parseHex(hex)
  if (rgb === null) return null
  const [r, g, b] = rgb.map(toLinear)

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)

  const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const bAxis = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s

  const chroma = Math.hypot(a, bAxis)
  const hue = ((Math.atan2(bAxis, a) * 180) / Math.PI + 360) % 360
  return { l: lightness, c: chroma, h: hue }
}

/** Shortest distance between two hues on the circle, in degrees. */
function hueDistance(a: number, b: number): number {
  const raw = Math.abs(a - b) % 360
  return raw > 180 ? 360 - raw : raw
}

/**
 * Whether two inks are too alike to sit side by side in one bar.
 *
 * Two rules, because the failure modes differ in kind. Two chromatic inks clash when hue *and*
 * lightness are close (Arsenal against United). Two achromatic inks have no meaningful hue, so they
 * clash on lightness alone (Newcastle against Bournemouth's black). Chromatic against achromatic
 * never clashes.
 *
 * An unparseable ink counts as not clashing: that is a bug in the table, and the table is the fix.
 */
export function inksClash(first: string, second: string): boolean {
  const a = hexToOklch(first)
  const b = hexToOklch(second)
  if (a === null || b === null) return false

  const aFlat = a.c < ACHROMATIC_CHROMA
  const bFlat = b.c < ACHROMATIC_CHROMA
  if (aFlat !== bFlat) return false
  if (aFlat) return Math.abs(a.l - b.l) < CLASH_LIGHTNESS
  if (hueDistance(a.h, b.h) >= CLASH_HUE_DEGREES) return false
  return Math.abs(a.l - b.l) < CLASH_LIGHTNESS_ESCAPE
}

/**
 * The two colours one fixture's bar renders in.
 *
 * **Both clubs use their primary.** That is the whole rule in the normal case.
 *
 * **On a clash, the away side uses its secondary.** Home always keeps its primary, so a club's
 * segment looks the same in every home fixture and only the away side ever moves. If away's
 * secondary clashes too it falls back to `--color-text-secondary`.
 */
export function fixtureInks(
  homeTeam: TeamId | string,
  awayTeam: TeamId | string
): { home: string; away: string } {
  const home = clubInks(homeTeam)
  const away = clubInks(awayTeam)
  // A club with no row can only be a relegated side that should never appear in a fixture, or a
  // gap in the table. Either way the neutral is honest and the bar still renders.
  const homeInk = home?.primary ?? INK_FALLBACK
  const awayPrimary = away?.primary ?? INK_FALLBACK
  if (!home || !away) return { home: homeInk, away: awayPrimary }

  if (!inksClash(homeInk, awayPrimary)) return { home: homeInk, away: awayPrimary }
  if (!inksClash(homeInk, away.secondary)) return { home: homeInk, away: away.secondary }
  return { home: homeInk, away: INK_FALLBACK }
}
